import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function Labels() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", contact_email: "" });

  const load = () => api.get("/labels").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const [q, setQ] = useState("");
  const filtered = rows.filter((l) => `${l.name} ${l.contact_email || ""}`.toLowerCase().includes(q.toLowerCase()));

  const approve = async (id) => {
    await api.post(`/labels/${id}/approve`);
    toast.success("Label approved");
    load();
  };

  const setShare = async (id) => {
    const v = window.prompt("Revenue share % this label/solo keeps (hidden from them):");
    if (v === null) return;
    await api.post("/revenue-share", { owner_type: "label", owner_id: id, share: Number(v) });
    toast.success("Revenue share set");
    load();
  };
  const adjust = async (id) => {
    const v = window.prompt("Set balance adjustment amount (₹, can be negative):");
    if (v === null) return;
    const note = window.prompt("Note for this adjustment:") || "";
    await api.post("/balance-adjust", { owner_type: "label", owner_id: id, amount: Number(v), note });
    toast.success("Balance adjusted");
    load();
  };

  const setStatus = async (id, action) => {
    if ((action === "suspend" || action === "terminate") &&
        !window.confirm(`This will ${action} the label and ALL its sub-labels and artists. Continue?`)) return;
    await api.post(`/labels/${id}/${action}`);
    toast.success(`Label ${action}d (cascaded to sub-labels & artists)`);
    load();
  };

  const create = async () => {
    if (!f.name) return toast.error("Name required");
    await api.post("/labels", f);
    toast.success("Label created");
    setOpen(false); setF({ name: "", contact_email: "" }); load();
  };

  return (
    <div className="space-y-6" data-testid="labels-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight">Labels</h1><p className="mt-1 text-muted-foreground">Manage record labels.</p></div>
        <Button onClick={() => setOpen(true)} data-testid="new-label-btn"><Plus className="mr-2 h-4 w-4" />Create Label</Button>
      </div>
      <Card className="p-4"><Input placeholder="Search labels by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="label-search" /></Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact Email</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`label-row-${l.id}`}>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell>{l.contact_email || "—"}</TableCell>
                <TableCell>
                  {l.status === "suspended" ? <Badge variant="outline" className="border-orange-500/30 bg-orange-500/10 text-orange-400">Suspended</Badge>
                    : l.status === "terminated" ? <Badge variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-400">Terminated</Badge>
                    : l.is_approved === false ? <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400">Pending</Badge>
                    : <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">Approved</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-wrap justify-end gap-2">
                    {l.is_approved === false && l.status !== "suspended" && l.status !== "terminated" && <Button size="sm" onClick={() => approve(l.id)} data-testid={`approve-label-${l.id}`}>Approve</Button>}
                    <Button size="sm" variant="secondary" onClick={() => setShare(l.id)} data-testid={`share-label-${l.id}`}>Share {l.revenue_share != null ? `${l.revenue_share}%` : ""}</Button>
                    <Button size="sm" variant="secondary" onClick={() => adjust(l.id)} data-testid={`adjust-label-${l.id}`}>₹ Adjust</Button>
                    {(l.status === "suspended" || l.status === "terminated")
                      ? <Button size="sm" onClick={() => setStatus(l.id, "reactivate")} data-testid={`reactivate-label-${l.id}`}>Reactivate</Button>
                      : <>
                          <Button size="sm" variant="secondary" onClick={() => setStatus(l.id, "suspend")} data-testid={`suspend-label-${l.id}`}>Suspend</Button>
                          <Button size="sm" variant="destructive" onClick={() => setStatus(l.id, "terminate")} data-testid={`terminate-label-${l.id}`}>Terminate</Button>
                        </>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Label</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="label-name" className="mt-1" /></div>
            <div><Label>Contact Email</Label><Input value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="label-email" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-label-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
